import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import * as styles from '../../styles.module.scss';
import { DataItem, DataTableColumn } from '../../utils/types';

interface TableRowsProps {
  tableData: DataItem[];
  columns: DataTableColumn[];
  renderAction?: (item: any) => React.ReactNode;
  stickyOffsets: { [index: number]: number };
  actionFixed: boolean;
}

const TableRows: React.FC<TableRowsProps> = ({
  tableData,
  columns,
  renderAction,
  stickyOffsets,
  actionFixed,
}) => {
  const { t } = useTranslation();

  const renderActionNodes = (item: any) => {
    if (!renderAction) return;
    return (
      <td
        className={classNames({
          [styles.stickyRight]: actionFixed,
        })}
        style={{
          right: 0,
          background: 'var(--tblr-bg-surface)',
        }}
      >
        {renderAction(item)}
      </td>
    );
  };

  const renderChild = (item: any, itemIndex: number) => {
    return (
      <tr key={`child-${itemIndex}`}>
        {columns
          .filter((col) => !col.isHide)
          .map((col, colIndex) => {
            let value = item[col.key];
            if (col.renderNode) {
              value = col.renderNode(item)
            }

            return (
              <td
                key={colIndex}
                className={classNames(
                  col.centered && 'text-center',
                  styles.bgSurface,
                  {
                    [styles.stickyLeft]: col.fixed === 'left',
                    [styles.stickyRight]: col.fixed === 'right',
                  },
                )}
                style={
                  col.fixed === 'left'
                    ? { left: stickyOffsets[colIndex] }
                    : col.fixed === 'right'
                      ? { right: stickyOffsets[colIndex] }
                      : {}
                }
              >
                {col.href ? (
                  <a target="_blank" href={`${col.href}/${value}`}>
                    <>{value}</>
                  </a>
                ) : (
                  <>{value}</>
                )}
              </td>
            );
          })}
        {renderActionNodes(item)}
      </tr>
    );
  };

  return useMemo(() => {
    if (!tableData.length) {
      return (
        <tr>
          <td colSpan={columns.length + 1} className="text-center">
            {t('NO_DATA')}
          </td>
        </tr>
      );
    }
    return tableData.map((item, index) => {
      if (item.datatableType === 'parent') {
        return (
          <>
            <tr key={`parent-${index}`} className="bg-light">
              <td colSpan={columns.length + 1} className="text-left fw-bold">
                {item.title}
              </td>
            </tr>
            {Array.isArray(item.children) &&
              item.children.map((child: any, childIndex: any) =>
                renderChild(child, childIndex),
              )}
          </>
        );
      }
      return renderChild(item, index);
    });
  }, [tableData, columns, renderAction, stickyOffsets]);
};

export default TableRows;
